import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { SERIES } from "../../data/content";
import CountryPicker from "./CountryPicker";

function scrollToTarget(hash) {
  const el = document.querySelector(hash);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function BuyMenu() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleSelect = (s) => {
    setOpen(false);
    // set hash so BooksShowcase can react and switch tabs, then scroll to books
    setTimeout(() => {
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, "", s.target);
      }
      // Manually dispatch hashchange since replaceState doesn't fire it
      window.dispatchEvent(new HashChangeEvent("hashchange"));
      const el = document.getElementById("books");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <div className="relative inline-block" ref={wrapRef}>
      <button
        type="button"
        className="btn-buy btn-buy-pulse !px-5 !py-3 md:!px-7"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="header-buy-button"
      >
        Buy Now
        <span
          className="inline-block transition-transform duration-300"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        >
          ↓
        </span>
      </button>

      <div
        role="menu"
        className={`absolute z-50 right-0 top-full mt-3 min-w-[280px] origin-top-right bg-[color:var(--paper)] border border-stone-900/15 shadow-[0_20px_60px_rgba(28,25,23,0.15)] transition-all duration-200 ${
          open
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-1 pointer-events-none"
        }`}
        data-testid="header-buy-menu"
      >
        <div className="px-4 pt-4 pb-2 text-[10px] tracking-[0.28em] uppercase text-stone-500 font-medium">
          Choose a series
        </div>
        <div className="h-px bg-stone-900/10" />
        <ul className="py-1">
          {SERIES.map((s) => {
            const soon = s.status !== "available";
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(s)}
                  className="w-full text-left px-4 py-3.5 flex items-center justify-between gap-4 hover:bg-stone-900 hover:text-[color:var(--paper)] transition-colors duration-150 outline-none group"
                  data-testid={`header-buy-series-${s.id}`}
                >
                  <span className="flex flex-col">
                    <span className="font-serif text-lg leading-tight">{s.name}</span>
                    <span className="text-[10px] tracking-[0.22em] uppercase opacity-60 mt-0.5">
                      Series № {s.number} · {s.note}
                    </span>
                  </span>
                  <span
                    className="text-xs opacity-70 group-hover:opacity-100"
                    aria-hidden="true"
                  >
                    {soon ? "◷" : "→"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default function Header({ country, onCountryChange }) {
  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 1.6, duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
      className="header-glass fixed top-0 inset-x-0 z-50"
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 md:py-5 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-3 shrink-0" data-testid="brand-logo">
          {/* Mobile: B badge (matches favicon) */}
          <span
            className="md:hidden relative inline-flex items-center justify-center w-9 h-9 rounded-[8px]"
            style={{ background: "var(--ink)" }}
            aria-label="Books for Little Hands"
          >
            <span
              className="font-serif italic font-bold text-xl leading-none"
              style={{ color: "var(--paper)" }}
            >
              B
            </span>
            <span
              className="absolute rounded-full"
              style={{
                width: "5px",
                height: "5px",
                top: "5px",
                right: "6px",
                background: "var(--accent-orange)",
              }}
            />
          </span>
          {/* Desktop: wordmark */}
          <span className="hidden md:inline font-serif italic text-[28px] leading-none">
            Books
          </span>
          <span className="hidden md:inline text-[10px] tracking-[0.35em] uppercase text-stone-600">
            for little hands
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8 text-[12px] uppercase tracking-[0.22em]">
          <a href="#series" className="hover:text-stone-500 transition-colors" data-testid="nav-series">
            The Series
          </a>
          <a href="#books" className="hover:text-stone-500 transition-colors" data-testid="nav-books">
            Books
          </a>
          <a href="#maya" className="hover:text-stone-500 transition-colors" data-testid="nav-maya">
            Meet Maya
          </a>
          <a href="#testimonials" className="hover:text-stone-500 transition-colors" data-testid="nav-testimonials">
            Reviews
          </a>
          <a href="#author" className="hover:text-stone-500 transition-colors" data-testid="nav-author">
            Creators
          </a>
        </nav>

        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <CountryPicker
            country={country}
            onChange={onCountryChange}
            align="right"
            compact
            testId="header-country-picker"
          />
          <a
            href="https://www.instagram.com/books.for.little.hands/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow Books for Little Hands on Instagram"
            className="inline-flex items-center justify-center w-10 h-10 border border-stone-900/25 rounded-full hover:bg-stone-900 hover:text-[color:var(--paper)] transition-colors duration-300"
            data-testid="header-instagram-link"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
            </svg>
          </a>
          <BuyMenu />
        </div>
      </div>
    </motion.header>
  );
}
